import React, { useCallback, useEffect, useState } from "react";
import { Grid } from "@mui/material";
import MultipleSelectDistributors from "../../../components/MultipleSelectDistributors/MultipleSelectDistributors";
import MultipleSelectCheckbox from "../../../components/MultipleSelectCheckbox";
import Select from "../../../components/Select/Select";
import CalendarRange from "../../../components/CalendarRange/CalendarRange";
import CustomButton, {
  CustomButtonVariant,
} from "../../../components/CustomButton/CustomButton";
import { useDistributor } from "../../../contexts/distributor/DistributorContext";
import { useOrderQuote } from "../../../contexts/orderQuote/OrderQuoteContext";
import { statusInvoiceList } from "../models/StatusInvoice";
import { convertToOptionsList } from "../../../utils";
import { convertToISO } from "../../../utils/dateFormatter";
import { GetAllFinancialQuery } from "../../../modules/financial/models/GetAllFinancialQuery";

const ROW_PER_PAGE = 10;

interface StatementFilterProps {
  onFilterChange: (filters: GetAllFinancialQuery | null) => void;
  onDownloadReport: () => void;
  isLoadingDownload: boolean;
  errorData: boolean;
}

const StatementFilter: React.FC<StatementFilterProps> = ({
  onFilterChange,
  onDownloadReport,
  isLoadingDownload,
  errorData,
}) => {
  const { getCategoryFromGroup } = useOrderQuote();
  const { distributors, selectedDistributorsStoraged } = useDistributor();

  const [categories, setCategories] = useState<any[]>([]);
  const [selectedCats, setSelectedCats] = useState<string[]>([]);
  const [status, setStatus] = useState<string>("ALL");
  const [clearCalendar, setClearCalendar] = useState(false);

  const currentDay = new Date();
  const firstDay = new Date(currentDay.getFullYear(), currentDay.getMonth(), 1);

  const [dateRange, setDateRange] = useState({
    begin: firstDay.toISOString(),
    end: currentDay.toISOString(),
  });

  const storageExternalIds = distributors
    .filter((ele) => selectedDistributorsStoraged.includes(ele.id as string))
    .map((ele) => ele.externalId)
    .join(",");

  const loadCategories = useCallback(async () => {
    const response = await getCategoryFromGroup?.();
    if (response) {
      setCategories(
        response.map((c) => ({ value: c.code, label: c.description }))
      );
    }
  }, [getCategoryFromGroup]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const buildQuery = useCallback(
    (): GetAllFinancialQuery => ({
      orderDirection: "desc",
      orderBy: "dueDate",
      service: "EXTRACT",
      statusInvoice: status === "ALL" ? "" : status,
      dueDateBegin: dateRange.begin,
      dueDateEnd: dateRange.end,
      customerNumber:
        storageExternalIds || distributors.map((d) => d.externalId).join(","),
      code: selectedCats.join(","),
      page: 1,
      limit: ROW_PER_PAGE,
    }),
    [status, dateRange, storageExternalIds, distributors, selectedCats]
  );

  const handleApplyFilters = () => {
    localStorage.setItem("isClickedInFilter", "true");
    onFilterChange(buildQuery());
  };

  useEffect(() => {
    const customerNumber =
      storageExternalIds || distributors.map((d) => d.externalId).join(",");
    if (customerNumber) {
      onFilterChange(buildQuery());
    }
  }, []);

  const handleClear = () => {
    const defaultDateRange = {
      begin: firstDay.toISOString(),
      end: currentDay.toISOString(),
    };
    const defaultStatus = "ALL";

    setSelectedCats([]);
    setStatus(defaultStatus);
    setDateRange(defaultDateRange);

    setClearCalendar(true);
    setTimeout(() => setClearCalendar(false), 100);

    const initialQuery: GetAllFinancialQuery = {
      orderDirection: "desc",
      orderBy: "dueDate",
      service: "EXTRACT",
      statusInvoice: "",
      dueDateBegin: defaultDateRange.begin,
      dueDateEnd: defaultDateRange.end,
      customerNumber:
        storageExternalIds || distributors.map((d) => d.externalId).join(","),
      code: "",
      page: 1,
      limit: ROW_PER_PAGE,
    };

    onFilterChange(initialQuery);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={3}>
        <MultipleSelectDistributors
          maxWidth={1200}
          label="Selecione o cliente"
        />
      </Grid>

      <Grid item xs={12} md={3}>
        <MultipleSelectCheckbox
          label="Categoria"
          options={convertToOptionsList(categories, "value", "label")}
          value={selectedCats}
          onChange={setSelectedCats}
        />
      </Grid>

      <Grid item xs={12} md={3}>
        <Select
          label="Status financeiro"
          isDisabled={!storageExternalIds}
          value={
            statusInvoiceList.find((i) => i.value === status) ||
            statusInvoiceList[0]
          }
          options={statusInvoiceList.map((item) => ({ option: item }))}
          getOptionItemLabel={(v) => v.label}
          getSelectedOptionLabel={(v) => v.label}
          onChange={(v) => setStatus(v.value)}
        />
      </Grid>

      <Grid item xs={12} md={3}>
        <CalendarRange
          name="date"
          label="Período de vencimento"
          disabledTextField={!storageExternalIds}
          clearValueCalendar={clearCalendar}
          value={[new Date(dateRange.begin), new Date(dateRange.end)]}
          onChangeDateStartEnd={(start, end) => {
            if (start !== null && end !== null) {
              setClearCalendar(false);
              setDateRange({
                begin: convertToISO(start),
                end: convertToISO(end),
              });
            }
          }}
        />
      </Grid>

      <Grid
        item
        xs={12}
        sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}
      >
        <CustomButton
          title="Baixar relatório"
          variant={
            isLoadingDownload
              ? CustomButtonVariant.OUTLINED_LOADING
              : errorData
              ? CustomButtonVariant.DISABLED
              : CustomButtonVariant.OUTLINED
          }
          disabled={!storageExternalIds || isLoadingDownload}
          onClick={onDownloadReport}
        />
        <CustomButton
          title="Limpar filtros"
          variant={CustomButtonVariant.OUTLINED}
          onClick={handleClear}
        />
        <CustomButton title="Consultar" onClick={handleApplyFilters} />
      </Grid>
    </Grid>
  );
};

export default StatementFilter;
